import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { KpiBand, KpiSection, StackBar, Tooltip } from '../ui';
import {
    LOT_EMPTY_TEXT, LOT_VALUE_TIPS, formatMoneyShort, formatMoneySigned, formatPctSigned, gapSeverity,
    lotCoverageText, summarizeLotValue,
} from '../../utils/lotValue';
import {
    BRANCH_OUTLIER_THRESHOLDS, alignmentLabels, alignmentTone, deltaVsDealership, inventoryLink, isUsedUnit, pctOf,
} from '../../utils/branchView';
import InventoryLink from './InventoryLink';
import { DEFAULT_AGING } from '../../utils/dealershipSettings';
import { useDealershipSettings } from '../../contexts/settingsContext';

// The dealership's aging buckets (the /combined counts under today's keys);
// each opens its units in the inventory with ?aging=<bucket id> (D14).
export const agingBucketsOf = (buckets) => buckets.map((b) => ({
    key: b.key, label: b.label, tone: b.tone, param: String(b.id),
}));
export const AGING_BUCKETS = agingBucketsOf(DEFAULT_AGING.buckets);
export const INTEREST_BUCKETS = [
    { key: 'hot', label: 'Hot', tone: 'red', param: 'Hot' },
    { key: 'warm', label: 'Warm', tone: 'amber', param: 'Warm' },
    { key: 'cold', label: 'Cold', tone: 'slate', param: 'Cold' },
    { key: 'no_interest', label: 'No Interest', tone: 'none', param: 'No Interest' },
];
export const RISK_BUCKETS = [
    { key: 'risk_high', label: 'High Risk', tone: 'red', param: 'High' },
    { key: 'risk_medium', label: 'Medium Risk', tone: 'amber', param: 'Medium' },
    { key: 'risk_low', label: 'Low Risk', tone: 'sage', param: 'Low' },
];
// Same windows as the backend's BuyerHeat (≤7 / 8–30 / 31–90 days).
export const INTEREST_LEGEND = 'Hot 7d · Warm 30d · Cold 90d';

/** A KPI bucket that opens its units in the inventory. */
function BucketLink({ to, value, label, sub, tone, title, ariaLabel }) {
    return (
        <InventoryLink to={to} className="v2-bk br2-bk" title={title} aria-label={ariaLabel}>
            <span className="v2-bk-n">{value}</span>
            <span className="v2-bk-l"><i className={`v2-bk-dot v2-tone-${tone}`} aria-hidden="true" />{label}</span>
            <span className="v2-bk-p">{sub}</span>
        </InventoryLink>
    );
}

BucketLink.propTypes = {
    to: PropTypes.string.isRequired,
    value: PropTypes.node.isRequired,
    label: PropTypes.node.isRequired,
    sub: PropTypes.node,
    tone: PropTypes.string.isRequired,
    title: PropTypes.string,
    ariaLabel: PropTypes.string,
};

// QA BR-4: the risk tile counts the /combined scored units (today's figure);
// the inventory scores every unit itself, so a drill there can differ.
export const riskTileTip = (scored) => `High, medium and low risk of ${scored.toLocaleString('en-US')} scored units, from the branch figures. The inventory scores every unit itself, so the drill-down there can show a slightly different count.`;
export const riskDrillTitle = (param) => `Opens all ${param.toLowerCase()}-risk units in the inventory`;

// No benchmark: no deltas and no gap reference.
const NO_REF = Object.freeze({
    label: null, aged: null, risk: null, gapName: null, gapPct: null,
});

function Delta({ value, dealer, thresholds, label }) {
    const d = deltaVsDealership(value, dealer, thresholds, label);
    return <span className={`br2-dlt br2-dlt--${d.tone}`} data-testid="delta">{d.text}</span>;
}

Delta.propTypes = {
    value: PropTypes.number.isRequired, dealer: PropTypes.number.isRequired, thresholds: PropTypes.object.isRequired, label: PropTypes.string,
};

const n = (v) => (v || 0).toLocaleString('en-US');
const OPEN_TITLE = 'Open these units in the inventory';

/**
 * B2: four tiles for the selection — aging, sell-speed risk, buyer interest
 * and used lot value — with today's labels and formulas. With a partial
 * selection the aged and high-risk shares carry a delta against the whole
 * dealership (coloured on the D15 outlier scale) and the lot tile shows the
 * dealership's gap. In compare mode with another basis, `reference`
 * ({ label, aged, risk, gapName, gapPct }) puts that benchmark (the compare
 * panel's own figures) in place of the dealership, so the page never shows
 * two different "vs" figures for one measure. `noReference` (a compare basis
 * with no benchmark yet: "Chosen branches" with nothing picked) shows no
 * deltas at all rather than falling back to the dealership. Every bucket
 * opens those units in the inventory.
 */
export default function SummaryBand({
    aggregated, dealership, allSelected, selectionUnits, alignment, linkBase, inventoryReady = true, reference = null,
    noReference = false,
}) {
    const b = aggregated;
    const { aging, gapThresholds } = useDealershipSettings();
    const agingBuckets = agingBucketsOf(aging.buckets);
    const scored = b.risk_high + b.risk_medium + b.risk_low;
    const lot = useMemo(() => {
        const used = selectionUnits.filter(isUsedUnit);
        const s = summarizeLotValue(used);
        let emptyText = null;
        if (!inventoryReady) emptyText = 'Loading inventory…';
        else if (used.length === 0) emptyText = LOT_EMPTY_TEXT.noUsed;
        else if (s.pricedCount === 0) emptyText = LOT_EMPTY_TEXT.noPaired;
        return {
            ...s,
            usedCount: used.length,
            hasNew: selectionUnits.some((u) => !isUsedUnit(u)),
            emptyText,
            severity: gapSeverity(s.gapPct, gapThresholds) || 'neutral',
        };
    }, [selectionUnits, inventoryReady, gapThresholds]);
    const link = (params) => inventoryLink({ ...linkBase, params });

    // The benchmark of the deltas: the whole dealership, the compare basis, or none.
    const ref = (noReference && NO_REF) || reference || {
        label: 'vs dealership',
        aged: dealership ? dealership.agedPct : null,
        risk: dealership ? dealership.riskPct : null,
        gapName: 'Dealership',
        gapPct: dealership?.gapPct ?? null,
    };

    const lotMax = Math.max(lot.askingTotal, lot.aiTotal) || 1;
    const lotBase = (Math.min(lot.askingTotal, lot.aiTotal) / lotMax) * 100;

    return (
        <KpiBand label="Branch summary" className="br2-band">
            <KpiSection
                title="Aging"
                meta={<><b>{n(b.total)}</b> units</>}
                bar={<StackBar segments={agingBuckets.map((a) => ({ key: a.key, tone: a.tone, value: b[a.key] || 0 }))} />}
            >
                <div className="v2-buckets br2-b4">
                    {agingBuckets.map((a) => (
                        <BucketLink
                            key={a.key}
                            to={link({ aging: a.param })}
                            value={n(b[a.key])}
                            label={a.label}
                            sub={`${pctOf(b[a.key], b.total)}%`}
                            tone={a.tone}
                            title={OPEN_TITLE}
                        />
                    ))}
                </div>
                <div className="br2-kfoot">
                    <span>Avg <b>{Math.round(b.avg_days_in_stock)}d</b> in stock</span>
                    {/* The share in the last aging bucket, named by its label (QA P2-5). */}
                    <span title={`Share of units in the last aging bucket (${agingBuckets[agingBuckets.length - 1].label})`}>
                        <b className={b.critical_pct >= 30 ? 'br2-ink-crit' : ''}>{b.critical_pct.toFixed(0)}%</b>
                        {' '}{agingBuckets[agingBuckets.length - 1].label}
                    </span>
                    {!allSelected && ref.aged != null && (
                        <Delta value={b.critical_pct} dealer={ref.aged} thresholds={BRANCH_OUTLIER_THRESHOLDS.aged} label={ref.label} />
                    )}
                </div>
            </KpiSection>

            <KpiSection
                title="Sell-Speed Risk"
                info={scored > 0 ? <Tooltip label="About Sell-Speed Risk" content={riskTileTip(scored)} /> : null}
                meta={<><b>{n(scored)}</b> scored</>}
                bar={scored > 0 ? (
                    <StackBar segments={RISK_BUCKETS.map((r) => ({ key: r.key, tone: r.tone, value: b[r.key] || 0 }))} />
                ) : null}
            >
                {scored > 0 ? (
                    <>
                        <div className="v2-buckets br2-b3">
                            {RISK_BUCKETS.map((r) => (
                                <BucketLink
                                    key={r.key}
                                    to={link({ risk: r.param })}
                                    value={n(b[r.key])}
                                    label={r.label}
                                    sub={`${pctOf(b[r.key], scored)}%`}
                                    tone={r.tone}
                                    title={riskDrillTitle(r.param)}
                                    ariaLabel={`${n(b[r.key])} ${r.label}, ${pctOf(b[r.key], scored)}%. ${riskDrillTitle(r.param)}`}
                                />
                            ))}
                        </div>
                        <div className="br2-kfoot">
                            <span><b className={b.pct_high_risk >= 30 ? 'br2-ink-crit' : ''}>{b.pct_high_risk.toFixed(0)}%</b> high-risk</span>
                            <span>{n(scored)} of {n(b.total)} scored</span>
                            {!allSelected && ref.risk != null && (
                                <Delta value={b.pct_high_risk} dealer={ref.risk} thresholds={BRANCH_OUTLIER_THRESHOLDS.slow} label={ref.label} />
                            )}
                        </div>
                    </>
                ) : (
                    <p className="br2-tile-empty">No units in this selection have a sell-speed risk score.</p>
                )}
            </KpiSection>

            <KpiSection
                title="Buyer Interest"
                meta={<><b className={b.active_pct > 0 ? 'br2-ink-good' : ''}>{b.active_pct.toFixed(0)}%</b> active</>}
                bar={<StackBar segments={INTEREST_BUCKETS.map((r) => ({ key: r.key, tone: r.tone, value: b[r.key] || 0 }))} />}
            >
                <div className="v2-buckets br2-b4">
                    {INTEREST_BUCKETS.map((r) => (
                        <BucketLink
                            key={r.key}
                            to={link({ interest: r.param })}
                            value={n(b[r.key])}
                            label={r.label}
                            sub={`${pctOf(b[r.key], b.total)}%`}
                            tone={r.tone}
                            title={OPEN_TITLE}
                        />
                    ))}
                </div>
                <div className="br2-kfoot">
                    <span><b>{n(b.total_quoted)}</b> of {n(b.total)} quoted</span>
                    <span>{INTEREST_LEGEND}</span>
                </div>
            </KpiSection>

            <KpiSection
                title="Used lot value"
                className="br2-lotk"
                info={lot.emptyText ? null : <Tooltip label="Coverage" content={lotCoverageText(lot)} />}
                meta={lot.emptyText ? null : `${n(lot.pricedCount)} of ${n(lot.usedCount)} priced`}
                bar={lot.emptyText ? null : (
                    <div className="br2-lotbar" aria-hidden="true">
                        <span className="br2-lotbar-ai" style={{ width: `${lotBase}%` }} />
                        <span
                            className={`br2-lotbar-gap ${lot.askingTotal >= lot.aiTotal ? 'is-over' : 'is-under'}`}
                            style={{ left: `${lotBase}%`, width: `${100 - lotBase}%` }}
                        />
                    </div>
                )}
            >
                {lot.emptyText ? (
                    <p className="br2-tile-empty">{lot.emptyText}</p>
                ) : (
                    <div className="br2-lot">
                        <Tooltip content={LOT_VALUE_TIPS.asking}>
                            <div className="br2-fig" tabIndex={0} data-testid="band-asking">
                                <span className="br2-fig-l">Asking</span>
                                <span className="br2-fig-n">{formatMoneyShort(lot.askingTotal)}</span>
                                <span className="br2-fig-p">list prices</span>
                            </div>
                        </Tooltip>
                        <Tooltip content={LOT_VALUE_TIPS.ai}>
                            <div className="br2-fig" tabIndex={0} data-testid="band-ai">
                                <span className="br2-fig-l">AI value</span>
                                <span className="br2-fig-n">{formatMoneyShort(lot.aiTotal)}</span>
                                <span className="br2-fig-p">model estimate</span>
                            </div>
                        </Tooltip>
                        <Tooltip content={`${LOT_VALUE_TIPS.gap}. Opens the inventory sorted by AI gap.`}>
                            <InventoryLink
                                to={link({ sort: 'gap' })}
                                className={`br2-fig br2-gap br2-gap--${lot.severity}`}
                                data-testid="band-gap"
                            >
                                <span className="br2-fig-l">Gap</span>
                                <span className="br2-fig-n">{formatMoneySigned(lot.gapTotal)}</span>
                                <span className="br2-fig-p">
                                    {formatPctSigned(lot.gapPct)}
                                    {lot.gapTotal ? (lot.gapTotal > 0 ? ' above AI' : ' below AI') : ''}
                                </span>
                            </InventoryLink>
                        </Tooltip>
                    </div>
                )}
                {(alignment || (!allSelected && ref.gapPct != null)) && (
                    <div className="br2-kfoot" data-testid="alignment">
                        {alignment && (
                            <>
                                <span>
                                    Avg Ask / AI Price{' '}
                                    <b className={`br2-ink-${alignmentTone(alignment.avg, gapThresholds)}`}>{alignment.avg}%</b>
                                </span>
                                <span><b className="br2-ink-warn">{alignment.overpriced}</b> Overpriced {alignmentLabels(gapThresholds).over}</span>
                                <span><b>{alignment.aligned}</b> Aligned {alignmentLabels(gapThresholds).aligned}</span>
                                <span><b className="br2-ink-below">{alignment.below}</b> Below {alignmentLabels(gapThresholds).below}</span>
                            </>
                        )}
                        {!allSelected && ref.gapPct != null && (
                            <span className="br2-dlt" data-testid="dealer-gap">{ref.gapName} {formatPctSigned(ref.gapPct)}</span>
                        )}
                    </div>
                )}
            </KpiSection>
        </KpiBand>
    );
}

SummaryBand.propTypes = {
    aggregated: PropTypes.object.isRequired,
    dealership: PropTypes.object,
    allSelected: PropTypes.bool.isRequired,
    selectionUnits: PropTypes.arrayOf(PropTypes.object).isRequired,
    alignment: PropTypes.object,
    linkBase: PropTypes.object.isRequired,
    inventoryReady: PropTypes.bool,
    noReference: PropTypes.bool,
    reference: PropTypes.shape({
        label: PropTypes.string, aged: PropTypes.number, risk: PropTypes.number, gapName: PropTypes.string, gapPct: PropTypes.number,
    }),
};

/**
 * The selection's own listings (P-BR-037, 052): count, average price, days
 * listed, longest listed (red from 180 days) and total listing value.
 * Hidden when the listing data has nothing for the selection. In compare
 * mode `reference` ({ name, avgPrice, avgDaysListed }) adds the benchmark's
 * listing-weighted averages.
 */
const money = (v) => `$${Math.round(v).toLocaleString('en-US')}`;

export function ListingStrip({ aggregated: b, reference }) {
    if (!b) return null;
    const ref = (v, fmt) => (reference && v != null ? `${reference.name} ${fmt(v)}` : null);
    const stats = [
        b.total_listings != null && { k: 'Listings', v: n(b.total_listings) },
        b.avg_price != null && { k: 'Avg Price', v: money(b.avg_price), r: ref(reference?.avgPrice, money) },
        b.avg_days_listed != null && {
            k: 'Avg Listed', v: `${Math.round(b.avg_days_listed)}d`, r: ref(reference?.avgDaysListed, (d) => `${Math.round(d)}d`),
        },
        b.max_days_listed != null && { k: 'Max Listed', v: `${b.max_days_listed}d`, crit: b.max_days_listed >= 180 },
        b.total_value != null && { k: 'Total listing value', v: `$${Math.round(b.total_value).toLocaleString('en-US')}` },
    ].filter(Boolean);
    if (!stats.length) return null;
    return (
        <div className="br2-strip" role="group" aria-label="Listing figures">
            <span className="v2-eyebrow">Listings</span>
            {stats.map((s) => (
                <span key={s.k} className={s.crit ? 'br2-strip-crit' : undefined}>
                    {s.k} <b>{s.v}</b>
                    {s.r && <span className="br2-strip-ref" data-testid="strip-ref" title={reference.title || undefined}> · {s.r}</span>}
                </span>
            ))}
        </div>
    );
}

ListingStrip.propTypes = {
    aggregated: PropTypes.object,
    reference: PropTypes.shape({
        name: PropTypes.string, avgPrice: PropTypes.number, avgDaysListed: PropTypes.number, title: PropTypes.string,
    }),
};
